import { CanActivate, Router, UrlTree } from '@angular/router';
import { InteractionStatus } from '@azure/msal-browser';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { Injectable } from '@angular/core';
import { filter, firstValueFrom, startWith, take, timeout } from 'rxjs';
import { AppLoadingService } from './app-loading.service';
import { EmployerAccessService } from './employer-access.service';

@Injectable({ providedIn: 'root' })
export class EmployerGuard implements CanActivate {
  constructor(
    private msalBroadcastService: MsalBroadcastService,
    private msalService: MsalService,
    private router: Router,
    private employerAccessService: EmployerAccessService,
    private loading: AppLoadingService
  ) {}

  async canActivate(): Promise<boolean | UrlTree> {
    this.loading.show('Logger inn og sjekker tilganger...');
    try {
      await firstValueFrom(
        this.msalBroadcastService.inProgress$.pipe(
          startWith(InteractionStatus.None),
          filter((status) => status === InteractionStatus.None),
          take(1),
          timeout(10000)
        )
      );
    } catch {
      return this.router.parseUrl('/auth');
    }

    const account = this.resolveAccount();
    if (!account) {
      return this.router.parseUrl('/auth');
    }

    this.msalService.instance.setActiveAccount(account);

    try {
      const hasAccess = await this.employerAccessService.ensureHasEmployerAccess();
      return hasAccess ? true : this.router.parseUrl('/apply-employer');
    } catch (error: any) {
      const msalCode = error?.errorCode || error?.code || '';
      if (
        ['interaction_required', 'login_required', 'consent_required', 'monitor_window_timeout'].includes(
          String(msalCode)
        )
      ) {
        return this.router.parseUrl('/auth');
      }
      return this.router.parseUrl('/apply-employer');
    } finally {
      this.loading.hide();
    }
  }

  private resolveAccount() {
    const activeAccount = this.msalService.instance.getActiveAccount();
    if (activeAccount) {
      return activeAccount;
    }

    const accounts = this.msalService.instance.getAllAccounts();
    if (accounts.length === 0) {
      return null;
    }

    const policy = sessionStorage.getItem('activePolicy')?.toLowerCase();
    if (policy) {
      const byPolicy = accounts.find((acc) => {
        const accountPolicy = (((acc.idTokenClaims as any)?.tfp || (acc.idTokenClaims as any)?.acr) ?? '').toLowerCase();
        return accountPolicy === policy;
      });
      if (byPolicy) {
        return byPolicy;
      }
    }

    return accounts[0];
  }
}
